import { useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_PRE_APPROVALS_ADD,
    SOCIETY_PORTAL_PRE_APPROVALS_EDIT,
} from '../../../../Routing/routes';
import {
    getPreApprovalsList,
    deletePreApproval,
} from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import useVmsList from '../useVmsList';
import useResidentPolicy from '../useResidentPolicy';
import VmsListShell from '../components/vms/VmsListShell';
import VmsTable from '../components/vms/VmsTable';
import VmsBadge from '../components/vms/VmsBadge';
import WingFlatPicker from '../components/vms/WingFlatPicker';
import ConfirmDialog from '../components/ConfirmDialog';
import { labelCls } from '../components/form/formStyles';
import {
    PRE_APPROVAL_STATUSES,
    PRE_APPROVAL_STATUS_STYLE,
    effectivePreApprovalStatus,
    isWindowPending,
    fmtDateTime,
    flatLabel,
    humanize,
    toOptions,
} from '../vmsConstants';
import { fmtPhone } from '../visitorConstants';

const STATUS_OPTIONS = toOptions(PRE_APPROVAL_STATUSES);

const PreApprovals = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const residentMayCreate = useResidentPolicy('allow_resident_pre_approval');
    const canCreate = can(PERMISSIONS.VMS_PRE_APPROVAL_CREATE) && residentMayCreate;
    const canUpdate = can(PERMISSIONS.VMS_PRE_APPROVAL_UPDATE);
    const canDelete = can(PERMISSIONS.VMS_PRE_APPROVAL_DELETE);

    const acting = useSelector(state => state.VmsReducer?.preApprovals?.acting);
    const [cancelTarget, setCancelTarget] = useState(null);

    const list = useVmsList({
        slice: 'preApprovals',
        action: getPreApprovalsList,
        filters: { status: '', wingId: '', flatId: '' },
    });

    const handleCancel = () => {
        dispatch(deletePreApproval({
            id: cancelTarget.pre_approval_id,
            onSuccess: () => { setCancelTarget(null); list.refresh(); },
        }));
    };

    const columns = [
        {
            key: 'visitor',
            header: 'Expected visitor',
            render: (p) => (
                <div className="tw-min-w-0">
                    <p className="tw-mb-0 tw-font-semibold tw-text-gray-800 tw-truncate">{p.visitor_name}</p>
                    <p className="tw-mb-0 tw-mt-0.5 tw-text-[10px] tw-text-gray-500 tw-font-mono">{fmtPhone(p.visitor_phone)}</p>
                </div>
            ),
        },
        { key: 'flat', header: 'Flat', nowrap: true, render: (p) => flatLabel(p) },
        { key: 'purpose', header: 'Purpose', render: (p) => humanize(p.purpose) },
        {
            key: 'window',
            header: 'Valid',
            nowrap: true,
            render: (p) => (
                <div className="tw-text-[10px] tw-leading-snug">
                    <p className="tw-mb-0">{fmtDateTime(p.valid_from)}</p>
                    <p className="tw-mb-0 tw-text-gray-500">to {fmtDateTime(p.valid_until)}</p>
                </div>
            ),
        },
        {
            key: 'verification',
            header: 'Verify',
            render: (p) => (
                p.verification_mode === 'OTP'
                    ? <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-violet-700"><Icon icon="mdi:shield-key-outline" /> OTP</span>
                    : <span className="tw-text-gray-500">{humanize(p.verification_mode)}</span>
            ),
        },
        {
            key: 'usage',
            header: 'Used',
            render: (p) => (
                <span className="tw-inline-flex tw-items-center tw-justify-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-gray-700 tw-font-semibold tw-text-[11px]">
                    {p.used_count ?? 0}/{p.max_usage_count ?? 1}
                </span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (p) => {
                const status = effectivePreApprovalStatus(p);
                return (
                    <span className="tw-inline-flex tw-items-center tw-gap-1">
                        <VmsBadge
                            value={status}
                            styles={PRE_APPROVAL_STATUS_STYLE}
                            title={status === 'EXPIRED' && p.status === 'ACTIVE'
                                ? 'Past its window — the gate will skip it, though the record still reads ACTIVE'
                                : undefined}
                        />
                        {isWindowPending(p.valid_from) && p.status === 'ACTIVE' && (
                            <span title="Window has not started yet">
                                <Icon icon="mdi:clock-start" className="tw-text-gray-400" />
                            </span>
                        )}
                    </span>
                );
            },
        },
        {
            key: 'actions',
            header: 'Action',
            align: 'right',
            stopPropagation: true,
            render: (p) => (
                <div className="tw-inline-flex tw-items-center tw-gap-1">
                    {canUpdate && p.status === 'ACTIVE' && (
                        <button
                            title="Edit"
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_PRE_APPROVALS_EDIT.replace(':id', p.pre_approval_id))}
                            className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-600"
                        >
                            <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                        </button>
                    )}
                    {canDelete && p.status === 'ACTIVE' && (
                        <button
                            title="Cancel pre-approval"
                            disabled={acting}
                            onClick={() => setCancelTarget(p)}
                            className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600 disabled:tw-opacity-50"
                        >
                            <Icon icon="mdi:cancel" className="tw-text-base" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    const filters = (
        <>
            <div>
                <label className={labelCls}>Status</label>
                <SearchableDropdown
                    small
                    value={list.filters.status}
                    onChange={(v) => list.setFilter('status', v === 'all' ? '' : v)}
                    options={STATUS_OPTIONS}
                    placeholder="All statuses"
                />
            </div>
            <WingFlatPicker
                clearable
                required={false}
                societyId={list.societyId}
                wingId={list.filters.wingId}
                flatId={list.filters.flatId}
                onWingChange={(v) => list.setFilter('wingId', v)}
                onFlatChange={(v) => list.setFilter('flatId', v)}
                wingPlaceholder="Any wing"
                flatPlaceholder="Any flat"
            />
            <div className="tw-flex tw-items-end">
                <button
                    type="button"
                    onClick={list.resetFilters}
                    disabled={!list.hasFilters}
                    className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-300 tw-bg-white tw-rounded hover:tw-bg-gray-50 disabled:tw-opacity-40"
                >
                    <Icon icon="mdi:filter-remove-outline" className="tw-text-base" />
                    Clear
                </button>
            </div>
        </>
    );

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            {/* The backend never sets EXPIRED, so a lapsed pre-approval keeps
                reading ACTIVE. The list recomputes it, and says so once here
                rather than in a tooltip on every row. */}
            <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-slate-50 tw-border tw-border-slate-200 tw-text-[11px] tw-text-slate-700">
                <Icon icon="mdi:information-outline" className="tw-text-base tw-shrink-0 tw-mt-px" />
                <span>
                    Statuses shown here account for the validity window. A pre-approval past its window is listed as
                    <strong> Expired</strong> and will be skipped at the gate, even though the stored status stays Active.
                </span>
            </div>

            <VmsListShell
                title="Pre-approvals"
                subtitle="Visitors a resident has authorised ahead of time, matched at the gate by phone and flat"
                itemLabel="pre-approvals"
                list={list}
                filters={filters}
                headerActions={canCreate && (
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_PRE_APPROVALS_ADD)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700"
                    >
                        <Icon icon="mdi:calendar-plus" className="tw-text-base" />
                        New Pre-approval
                    </button>
                )}
            >
                <VmsTable
                    columns={columns}
                    rows={list.rows}
                    loading={list.loading}
                    loaderLabel="Loading pre-approvals..."
                    rowKey={(p) => p.pre_approval_id}
                    empty={{
                        icon: 'mdi:calendar-clock-outline',
                        filtered: list.hasFilters,
                        filteredTitle: 'No pre-approvals match these filters',
                        emptyTitle: 'No pre-approvals yet',
                        emptyHint: 'A pre-approval lets an expected visitor through without the resident being called — it is matched at the gate on their phone number and flat.',
                        onClear: list.resetFilters,
                    }}
                />
            </VmsListShell>

            <ConfirmDialog
                open={!!cancelTarget}
                title="Cancel this pre-approval?"
                subtitle="The visitor will no longer be let through automatically."
                confirmLabel="Cancel pre-approval"
                busyLabel="Cancelling..."
                tone="danger"
                icon="mdi:cancel"
                busy={acting}
                onCancel={() => setCancelTarget(null)}
                onConfirm={handleCancel}
            >
                <p className="tw-mb-0">
                    <span className="tw-font-semibold">{cancelTarget?.visitor_name}</span>
                    {cancelTarget ? ` (${fmtPhone(cancelTarget.visitor_phone)})` : ''} will need the resident&apos;s
                    approval at the gate instead.
                </p>
            </ConfirmDialog>
        </div>
    );
};

export default PreApprovals;
